import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, X, Check, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const post = (file, dryRun) => {
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(`${API}/admin/products/import-stock?dry_run=${dryRun}`, fd, { withCredentials: true })
    .then((r) => r.data);
};

export function ImportStockButton({ onImported }) {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const fail = (err) => {
    const d = err.response?.data?.detail;
    toast.error(typeof d === "string" ? d : "Could not read that spreadsheet");
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      setPreview(await post(f, true));
      setFile(f);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await post(file, false);
      toast.success(`Stock updated for ${res.changes.length} product${res.changes.length === 1 ? "" : "s"}`);
      setPreview(null);
      setFile(null);
      onImported();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const close = () => { setPreview(null); setFile(null); };

  return (
    <>
      <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={pick} data-testid="import-stock-file" />
      <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid="import-stock-button"
        className="hard-shadow hard-shadow-hover inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-white px-5 py-2.5 font-display text-sm font-extrabold uppercase disabled:opacity-60">
        {busy && !preview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import counts
      </button>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0A]/60 p-4" data-testid="import-stock-dialog">
          <div className="hard-shadow flex max-h-[85vh] w-full max-w-lg flex-col rounded-2xl border-2 border-[#0A0A0A] bg-white">
            <div className="flex items-start justify-between gap-3 border-b-2 border-[#0A0A0A] p-5">
              <div>
                <h3 className="font-display text-xl font-extrabold tracking-tight">Review stock changes</h3>
                <p className="mt-1 text-xs text-[#4A4A4A]">{file?.name} · {preview.changes.length} to update · {preview.unchanged} unchanged</p>
              </div>
              <button onClick={close} data-testid="import-stock-close" className="rounded-full border-2 border-[#0A0A0A] p-1 hover:bg-[#FFD700]"><X className="h-4 w-4" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {preview.changes.length === 0 ? (
                <p data-testid="import-stock-nochanges" className="text-center text-sm font-medium text-[#4A4A4A]">No stock numbers differ from what&rsquo;s already in the store.</p>
              ) : (
                <ul className="flex flex-col gap-2" data-testid="import-stock-changes">
                  {preview.changes.map((c) => (
                    <li key={c.id} data-testid={`import-change-${c.slug}`} className="flex items-center justify-between gap-3 rounded-lg border-2 border-[#0A0A0A]/15 px-3 py-2 text-sm">
                      <span className="font-bold leading-tight">{c.name} <span className="text-[10px] font-extrabold uppercase text-[#4A4A4A]">{c.tag}</span></span>
                      <span className="inline-flex items-center gap-1.5 font-extrabold whitespace-nowrap">
                        <span className="text-[#4A4A4A]">{c.old}</span><ArrowRight className="h-3.5 w-3.5" />
                        <span className={c.new === 0 ? "text-[#FF2A54]" : "text-[#16a34a]"}>{c.new}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {preview.invalid.length > 0 && (
                <div className="mt-4 rounded-lg border-2 border-[#FF2A54] bg-[#FF2A54]/10 p-3 text-xs" data-testid="import-stock-invalid">
                  <p className="font-extrabold uppercase text-[#FF2A54]">Skipped — bad stock value</p>
                  {preview.invalid.map((r) => <p key={r.line}>Line {r.line}: {r.name} → &ldquo;{r.value}&rdquo;</p>)}
                </div>
              )}
              {preview.unmatched.length > 0 && (
                <div className="mt-4 rounded-lg border-2 border-[#0A0A0A]/20 bg-[#FFF7DB] p-3 text-xs" data-testid="import-stock-unmatched">
                  <p className="font-extrabold uppercase">Not in store (skipped)</p>
                  {preview.unmatched.map((r) => <p key={r.line}>Line {r.line}: {r.name}</p>)}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 border-t-2 border-[#0A0A0A] p-5">
              <button onClick={close} className="rounded-full border-2 border-[#0A0A0A] px-4 py-2 text-xs font-extrabold uppercase hover:bg-[#FFD700]">Cancel</button>
              <button onClick={apply} disabled={busy || preview.changes.length === 0} data-testid="import-stock-apply"
                className="hard-shadow hard-shadow-hover inline-flex items-center gap-1.5 rounded-full border-2 border-[#0A0A0A] bg-[#22c55e] px-4 py-2 text-xs font-extrabold uppercase text-white disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Apply {preview.changes.length} change{preview.changes.length === 1 ? "" : "s"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
